import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { BaseService } from 'src/app/helpers/base.service';
import { ConnectionService } from 'src/app/helpers/offline-detection';
import { ConfigLoaderService } from 'src/app/config-loader.service';

export const baseBackgroundUrl = 'https://loomis-assets.s3-us-west-2.amazonaws.com/Backgrounds/';
export const defaultBackgroundUrl = `${baseBackgroundUrl}0.jpg`;

@Injectable({
  providedIn: 'root'
})
export class BackgroundPhotoService extends BaseService {
  private readonly cookieName = 'background-photos-cache';
  private readonly cookieExpirationHours = 24; // 24 hours

  constructor(
    http: HttpClient,
    connectionService: ConnectionService,
    configService: ConfigLoaderService,
    private cookieService: CookieService
  ) {
    super(http, connectionService, configService);
  }

  getBackgroundPhotos(): Observable<string[]> {
    // Check cookie cache (persists across page refreshes)
    const photoIds = this.getFromCookie();
    if (photoIds) {
      return of(photoIds);
    }

    const backgroundApiUrl = `${this.apiUrl}/Photo/GetBackgroundPhotos`;
    // Fetch from API and cache the result
    return this.http.get<string[]>(backgroundApiUrl).pipe(
      tap(photoIds => {
        this.updateCache(photoIds);
      }),
      catchError(error => {
        const photoIds = this.getFromCookie();
        if (photoIds) {
          console.warn('Background photos API failed, using stale cache data', error);
          return of(photoIds);
        }
        throw error;
      })
    );
  }

  private getFromCookie(): string[] | null {
    const cached = this.cookieService.get(this.cookieName);
    
    if (!cached) return null;

    try {
      const parsedData: string[] = JSON.parse(cached);
      return parsedData;
    } catch (error) {
      // Invalid JSON, remove the cookie
      this.cookieService.delete(this.cookieName, '/');
      return null;
    }
  }

  private updateCache(photoIds: string[]): void {
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + (this.cookieExpirationHours * 60 * 60 * 1000));
    
    this.cookieService.set(
      this.cookieName,
      JSON.stringify(photoIds),
      expirationDate,
      '/',
      undefined,
      false,
      'Lax'
    );
  }

  getRandomBackgroundUrl(): Observable<string> {
    return this.getBackgroundPhotos().pipe(
      map(photos => {
        if (!photos || photos.length === 0) {
          // Fallback to default background if no photos available
          return `url("${defaultBackgroundUrl}") no-repeat center center fixed`;
        }

        const randomPhotoId = photos[Math.floor(Math.random() * photos.length)];
        return `url("${baseBackgroundUrl}${randomPhotoId}") no-repeat center center fixed`;
      })
    );
  }
}